import { Ionicons } from '@expo/vector-icons';
import { Link } from 'expo-router';
import React from 'react';
import { Image, View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { formatBRL } from '@/content/facts';
import { PressableCard } from '@/components/ui/card';
import { ProviderCard as ProviderCardType } from '@/types/discovery';
import { cn } from '@/utils/cn';

const DEFAULT_BRAND = '#FD5001';

/** "R$ 3,50": com centavos (arredondar mostrava "R$ 4" para quem cobra R$ 3,50). */
const formatPrice = (value: number): string => formatBRL(value);

function locationLabel(card: ProviderCardType): string | null {
  const parts = [card.neighborhood, card.city].filter(Boolean);
  if (parts.length === 0) return null;
  return card.state ? `${parts.join(', ')} · ${card.state}` : parts.join(', ');
}

type ProviderCardProps = {
  card: ProviderCardType;
  /** Com `href`, o toque navega pelo link; `onPress` fica só para métricas. */
  onPress?: () => void;
  /** Destino do card. Na web vira `<a href>` de verdade, que buscador e crawler seguem. */
  href?: string;
  /** Largura fixa no carrossel; a busca deixa o card ocupar a coluna. */
  className?: string;
};

/**
 * Mini-perfil do prestador: capa, logo e cor da marca que ele próprio configurou.
 * O card é a prévia fiel da vitrine que abre ao tocar.
 */
export function ProviderCard({ card, onPress, href, className }: ProviderCardProps) {
  const brand = card.brandColor || DEFAULT_BRAND;
  const location = locationLabel(card);

  const content = (
    <PressableCard
      onPress={onPress}
      accessibilityRole={href ? 'link' : 'button'}
      className={cn('overflow-hidden', className)}
    >
      <View className="h-24 w-full" style={{ backgroundColor: `${brand}1A` }}>
        {card.coverUrl ? (
          <Image
            source={{ uri: card.coverUrl }}
            aria-hidden
            className="h-full w-full"
            resizeMode="cover"
          />
        ) : null}

        {card.isSponsored ? (
          <View className="absolute right-2 top-2 rounded-full bg-black/60 px-2 py-0.5">
            <ThemedText className="text-[10px] font-semibold text-white">
              Patrocinado
            </ThemedText>
          </View>
        ) : null}
      </View>

      <View className="gap-2 p-3">
        <View className="-mt-8 flex-row items-end gap-2">
          <View
            className="h-12 w-12 items-center justify-center overflow-hidden rounded-2xl border-2 border-card bg-card"
            style={{ borderCurve: 'continuous' }}
          >
            {card.logoUrl ? (
              <Image
                source={{ uri: card.logoUrl }}
                alt={`Logo de ${card.businessName}`}
                className="h-full w-full"
                resizeMode="cover"
              />
            ) : (
              <View
                className="h-full w-full items-center justify-center"
                style={{ backgroundColor: `${brand}1A` }}
              >
                <ThemedText className="text-base font-bold" style={{ color: brand }}>
                  {card.businessName.charAt(0).toUpperCase()}
                </ThemedText>
              </View>
            )}
          </View>

          {card.isBookable ? (
            <View className="mb-0.5 flex-row items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5">
              <Ionicons className="text-emerald-600" name="calendar-outline" size={11} />
              <ThemedText className="text-[10px] font-semibold text-emerald-700">
                Agenda online
              </ThemedText>
            </View>
          ) : null}
        </View>

        <View className="gap-0.5">
          <ThemedText numberOfLines={1} className="font-semibold text-foreground">
            {card.businessName}
          </ThemedText>

          {card.category ? (
            <ThemedText numberOfLines={1} className="text-xs text-neutral-500">
              {card.category}
            </ThemedText>
          ) : null}

          {location ? (
            <View className="flex-row items-center gap-1">
              <Ionicons className="text-neutral-400" name="location-outline" size={11} />
              <ThemedText numberOfLines={1} className="text-xs text-neutral-400">
                {location}
              </ThemedText>
            </View>
          ) : null}
        </View>

        {card.services.length > 0 ? (
          <View className="flex-row flex-wrap gap-1">
            {card.services.slice(0, 2).map((service) => (
              <View key={service.id} className="rounded-full bg-neutral-100 px-2 py-0.5">
                <ThemedText numberOfLines={1} className="text-[10px] text-neutral-600">
                  {service.name}
                </ThemedText>
              </View>
            ))}
          </View>
        ) : null}

        {card.priceFrom !== null ? (
          <ThemedText className="text-xs text-neutral-500">
            A partir de{' '}
            <ThemedText className="font-semibold text-foreground">
              {formatPrice(card.priceFrom)}
            </ThemedText>
          </ThemedText>
        ) : null}
      </View>
    </PressableCard>
  );

  return href ? (
    <Link href={href as never} asChild>
      {content}
    </Link>
  ) : (
    content
  );
}
